/**
 * pages/Profile.js — Phase 74B deep-dive on the 8 Financial DNA attributes.
 *
 * Fetches /api/snapshot, reads the `dna` block, and renders one card per
 * attribute with:
 *   • tone dot + label + score
 *   • score bar (0–100)
 *   • one-line descriptor
 *   • "how this is computed" collapsible
 *   • raw input ratios for the curious
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Fingerprint, Sparkles, Compass } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TONE_COLORS = {
  strong:   'bg-emerald-500',
  building: 'bg-amber-500',
  watch:    'bg-rose-500',
  muted:    'bg-muted-foreground/30',
};
const TONE_TEXT = {
  strong:   'text-emerald-700 dark:text-emerald-400',
  building: 'text-amber-700 dark:text-amber-400',
  watch:    'text-rose-700 dark:text-rose-400',
  muted:    'text-muted-foreground',
};
const TONE_BAR = {
  strong:   'bg-emerald-500',
  building: 'bg-amber-500',
  watch:    'bg-rose-500',
  muted:    'bg-muted-foreground/30',
};

function ScoreBar({ score, tone }) {
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score));
  return (
    <div className="h-1.5 rounded-full bg-muted/60 w-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${TONE_BAR[tone] || TONE_BAR.muted}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function Profile() {
  usePageTitle('Your Financial DNA');
  const { session, profile } = useAuth();
  const token = session?.access_token;

  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${BACKEND_URL}/api/snapshot`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        if (!cancelled) setSnapshot(r.data);
      } catch (e) {
        console.warn('snapshot load failed:', e?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const dna = snapshot?.dna;
  const attrs = dna?.attributes || [];
  const overall = dna?.overall_score;
  const overallTone = dna?.overall_tone || 'muted';

  const lifeCtx = snapshot?.life_context || {};
  const flags = snapshot?.flags || {};

  const summary = useMemo(() => {
    if (loading) return null;
    if (overall == null) return "We don't have enough signal yet to read your DNA. Add a few transactions and fill in your Life Profile.";
    if (overallTone === 'strong')   return "The building blocks are in a strong place. The next lift comes from consistency.";
    if (overallTone === 'building') return "You're actively building. Focus on the attributes marked 'watch' first — they move the overall score the most.";
    return "A few load-bearing attributes need attention. Start with runway and debt — those two move the whole picture.";
  }, [loading, overall, overallTone]);

  return (
    <div className="space-y-5" data-testid="profile-root">
      {/* Header */}
      <div>
        <div className="text-[11px] uppercase tracking-[2px] text-gold flex items-center gap-2">
          <Fingerprint className="h-3 w-3" /> Your Financial DNA
        </div>
        <h1 className="font-display text-2xl font-semibold mt-1" data-testid="profile-title">
          {profile?.full_name ? `${profile.full_name.split(' ')[0]}'s money profile` : 'Your money profile'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Eight signals — deterministic, no AI — that add up to a picture of how your money moves today.
        </p>
      </div>

      {/* Overall summary */}
      <Card className="rounded-3xl border-0 bg-brand-gradient text-primary-foreground shadow-brand-lg" data-testid="profile-summary">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[2px] text-gold">Overall shape</div>
              <div className="font-display text-3xl font-semibold mt-1" data-testid="profile-overall-score">
                {overall == null ? '—' : `${Math.round(overall)}/100`}
              </div>
              <div className="text-sm text-primary-foreground/70 mt-1 capitalize">
                {overallTone === 'muted' ? 'Not enough data yet' : overallTone}
              </div>
            </div>
            <p className="text-sm text-primary-foreground/80 leading-relaxed max-w-md">
              {loading ? '—' : summary}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Attribute grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-2xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="profile-attributes">
          {attrs.map((a) => (
            <Card key={a.key} className="rounded-2xl border-border/70" data-testid={`profile-attr-${a.key}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${TONE_COLORS[a.tone] || TONE_COLORS.muted}`} aria-hidden />
                    <span className="text-sm font-medium">{a.label}</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-lg font-semibold ${TONE_TEXT[a.tone]}`}>
                      {a.score == null ? '—' : Math.round(a.score)}
                    </span>
                    <span className={`text-[11px] uppercase tracking-wide ${TONE_TEXT[a.tone]}`}>
                      {a.tone === 'muted' ? 'muted' : a.tone}
                    </span>
                  </div>
                </div>
                <ScoreBar score={a.score} tone={a.tone} />
                <p className="text-sm text-muted-foreground leading-relaxed">{a.descriptor}</p>

                <Accordion type="single" collapsible className="pt-1">
                  <AccordionItem value="how" className="border-none">
                    <AccordionTrigger className="text-xs text-muted-foreground hover:no-underline py-1 [&[data-state=open]>svg]:rotate-180">
                      How this is computed
                    </AccordionTrigger>
                    <AccordionContent className="text-xs text-muted-foreground pt-2 leading-relaxed">
                      {a.how}
                      {a.inputs && Object.keys(a.inputs).length > 0 && (
                        <div className="mt-2 rounded-lg bg-muted/40 p-2 font-mono text-[10px] whitespace-pre-wrap">
                          {Object.entries(a.inputs).map(([k, v]) => (
                            <div key={k}>
                              <span className="text-muted-foreground">{k}:</span> {String(v)}
                            </div>
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Life Profile summary sidebar-lite */}
      {!loading && (
        <Card className="rounded-2xl border-border/70" data-testid="profile-life-summary">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Compass className="h-4 w-4 text-primary" />
              <div className="text-sm font-medium">Life Profile snapshot</div>
              <a href="/settings#life-profile" className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 ml-auto">Update</a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <LifeRow label="Profile quality"   value={lifeCtx.profile_quality || '—'} />
              <LifeRow label="Employment"        value={lifeCtx.employment_stability || '—'} />
              <LifeRow label="Housing"           value={lifeCtx.housing_status || '—'} />
              <LifeRow label="Risk appetite"     value={lifeCtx.risk_appetite || '—'} />
              <LifeRow label="Retirement target" value={lifeCtx.retirement_age_target ?? '—'} />
              <LifeRow label="Confidence"        value={lifeCtx.financial_confidence != null ? `${lifeCtx.financial_confidence}/5` : '—'} />
              <LifeRow label="Dependents"        value={flags.has_dependents ? 'yes' : 'no'} />
              <LifeRow label="Health insurance"  value={flags.has_health_insurance ? 'yes' : '—'} />
              <LifeRow label="Life insurance"    value={flags.has_life_insurance ? 'yes' : '—'} />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function LifeRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm capitalize">{typeof value === 'string' ? value.replace(/_/g, ' ') : String(value)}</span>
    </div>
  );
}
